import React, { useState, useRef, useEffect } from "react";
import { Button } from "@pineseed/gui/component/button";
import { Send } from "lucide-react";
import { cn } from "@pineseed/gui/util/utils";

interface ChatControlProps {
  className?: string;
  // textInputHeight: number;
  onKeyDown: (e: React.KeyboardEvent) => void;
  showGlobalLoading: boolean;
  onEditorReady: (
    getText: () => string,
    getContentWithTags: () => string,
    clearText: () => void,
    initialHasText: boolean,
    setText: (text: string) => void,
  ) => void;
  onTextChange: (newHasText: boolean) => void;
  handleSubmit: () => void;
  hasText: boolean;
  sendOnEnter?: boolean;
  setSendOnEnter?: (value: boolean) => void;
  isConnected: boolean;
}

export function ChatControl({
  className,
  // textInputHeight,
  // onKeyDown,
  showGlobalLoading,
  // onEditorReady,
  // onTextChange,
  handleSubmit,
  hasText,
  sendOnEnter,
  setSendOnEnter,
  isConnected,
}: ChatControlProps) {
  const title = sendOnEnter ? "Enter to send" : "Ctrl/Cmd/Alt+Enter to send";
  // const title = "Toggle send behavior";
  const [tooltip, setTooltip] = useState<{ show: boolean; transitioning: boolean; x: number; y: number; message: string }>({
    show: false,
    transitioning: false,
    x: 0,
    y: 0,
    message: "",
  });
  const resetTooltip = () => {
    setTooltip({
      show: false,
      transitioning: false,
      x: 0,
      y: 0,
      message: "",
    });
  };

  const timeoutRefOuter = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const timeoutRefInner = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const toggleEnabled = !!setSendOnEnter;

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setSendOnEnter?.(newValue);

    // Clear existing timeouts
    if (timeoutRefInner.current) {
      clearTimeout(timeoutRefInner.current);
    }
    if (timeoutRefOuter.current) {
      clearTimeout(timeoutRefOuter.current);

    }

    // Show tooltip at mouse position
    const message = newValue
      ? "Enter key will send messages"
      : "Enter key will add new lines";

    setTooltip({
      show: true,
      transitioning: false,
      x: e.target.getBoundingClientRect().left,
      y: e.target.getBoundingClientRect().bottom,
      message,
    });

    // Hide tooltip after delay
    timeoutRefOuter.current = setTimeout(() => {
      console.log("timeoutRefOuter.current", timeoutRefOuter.current);
      setTooltip(prev => ({ ...prev, show: true, transitioning: true }));
      timeoutRefInner.current = setTimeout(() => {
        console.log("timeoutRefInner.current", timeoutRefInner.current);
        resetTooltip();
      }, 1000); // 1 second delay
    }, 2500);
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Tooltip */}
      {/* {tooltip.show && ( */}
      {/* {tooltip.show && ( */}
      <div
        className="absolute z-50 p-2 text-xs text-center bg-background border border-border rounded-md transition-all duration-200 ease-in-out animate-in fade-in-0"
        style={{
          left: `${tooltip.x}px`,
          top: `${tooltip.y}px`,
          transform: 'translateX(-6rem) translateY(-8rem)',
          width: (toggleEnabled && tooltip.show) ? '6rem' : '0',
          height: (toggleEnabled && tooltip.show) ? '4rem' : '0',
          opacity: (toggleEnabled && tooltip.show && !tooltip.transitioning) ? 1 : 0,
        }}
        onClick={resetTooltip}
      >
        {tooltip.message}
      </div>
      {/* )} */}

      <Button
        onClick={handleSubmit}
        disabled={!hasText || showGlobalLoading || !isConnected}
        size="sm"
        className="button-primary"
      >
        <Send className="w-3 h-3 mr-1" />
        Send
      </Button>

      {/* Send behavior toggle */}
      {toggleEnabled && (
        <label
          className="flex items-center text-xs text-muted-foreground cursor-pointer"
          title={title}
        >
          <div className="relative mr-1">
            <input
              type="checkbox"
              checked={sendOnEnter}
              onChange={handleToggleChange}
              className="sr-only"
            />
            <div className="w-3 h-8 rounded-full border border-primary transition-colors">
              <div
                className="w-2 h-3 bg-secondary border border-secondary rounded-full shadow-sm transition-transform transform"
                style={{
                  transform: sendOnEnter ? "translateY(1px) translateX(1px)" : "translateY(16px) translateX(1px)",
                }}
              />
            </div>
          </div>
        </label>
      )}
    </div>
  );
}
